import React from 'react';
import { Button } from 'antd';
import { ClockCircleOutlined, DeleteOutlined, CloseOutlined, FolderOutlined, ProjectOutlined } from '@ant-design/icons';
import { BacklogCircle } from '../../../assets/CustomIcons/BacklogCircle';
import { SprintIcon } from '../../../assets/CustomIcons/SprintIcon';
import { WorkspaceFramework } from '../../../types/Workspace';
import { EditField, EditFieldsDropdown } from './EditFieldsDropdown';
import { Epic } from '../../../types/Epic';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import { SprintStatusType } from '../../UI/SprintStatusLabel';
import { TaskStage } from '../../../types/Task';
import { TaskStatus } from '../../../types/TaskStatus';
import { TaskType } from '../../../types/TaskType';
import { SpaceLabel } from '../../../types/TaskLabel';
import { PriorityOptions } from '../../TaskTable/helpers/helper';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../../../types/WorkspaceView';

interface ActionPanelProps {
  selectedItems: number[];
  onDeleteSelected: () => void;
  onBatchEdit: (
    fieldType: EditField,
    field:
      | SprintStatusType
      | TaskStage
      | TaskType
      | Epic
      | WorkspaceAccessResponse
      | TaskStatus
      | SpaceLabel[]
      | (Date | undefined)
      | PriorityOptions
      | number,
    action?: 'add' | 'remove'
  ) => void;
  setSelectedItems: (items: number[]) => void;
  framework: WorkspaceFramework;
  epics: Epic[];
  usersWithAccessToSpace: WorkspaceAccessResponse[];
}

const ActionPanel: React.FC<ActionPanelProps> = ({
  selectedItems,
  onDeleteSelected,
  onBatchEdit,
  setSelectedItems,
  framework,
  epics,
  usersWithAccessToSpace,
}) => {
  const viewContext = useViewContext();
  const viewGroup = viewContext?.view?.group;
  const isTriage = viewGroup === WorkspaceViewGroup.triage;
  const isArchive = viewGroup === WorkspaceViewGroup.archive;
  const isSprintView = viewGroup === WorkspaceViewGroup.sprint;
  const isKanbanView = viewGroup === WorkspaceViewGroup.kanban;
  const isEpicEntity = viewContext?.view?.entityType === WorkspaceViewEntityType.epic;

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[99] flex items-center">
      <div className="rounded-xl shadow bg-taskonioGreen-300 border-solid border-4 border-taskonioGreen-300 flex-col justify-start items-start gap-2.5 inline-flex">
        <div className="h-[66px] self-stretch px-6 bg-white rounded-xl border-solid border-2 border-taskonioGreen-600 justify-start items-center gap-6 inline-flex">
          <div className="flex-col justify-center items-center gap-0.5 inline-flex">
            <div className="text-brand-grey-dark text-[10px] font-medium leading-3">Selected</div>
            <div className="text-brand-grey-dark text-xl font-semibold leading-7">{selectedItems.length}</div>
          </div>
          <div className="w-px h-[42px] bg-neutral-300 rounded-sm" />
          <div className="justify-start items-start gap-2 flex">
            {!isEpicEntity && (
              <div className="justify-start items-start gap-2 flex">
                <EditFieldsDropdown
                  framework={framework}
                  epics={epics}
                  usersWithAccessToSpace={usersWithAccessToSpace}
                  selectedItems={selectedItems}
                  onSelected={onBatchEdit}
                />
                {isTriage && (
                  <Button
                    size="large"
                    type="primary"
                    className="rounded-lg items-center gap-2 flex text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.stage, 'backlog')}
                  >
                    <BacklogCircle className="w-4 h-4" />
                    Accept → Backlog
                  </Button>
                )}
                {isArchive && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.stage, 'backlog')}
                  >
                    <BacklogCircle className="w-4 h-4" />
                    Restore to Backlog
                  </Button>
                )}
                {!isArchive && !isTriage && framework === WorkspaceFramework.scrum && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.sprintStatus, 'planning')}
                  >
                    <ClockCircleOutlined className="w-4 h-4" />
                    Move to Planning
                  </Button>
                )}
                {!isArchive && !isTriage && isSprintView && framework === WorkspaceFramework.scrum && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.sprintStatus, 'none')}
                  >
                    <CloseOutlined className="w-4 h-4" />
                    Remove from Sprint
                  </Button>
                )}
                {!isArchive && !isTriage && !isSprintView && framework === WorkspaceFramework.scrum && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.sprintStatus, 'sprint')}
                  >
                    <SprintIcon className="w-4 h-4" />
                    Add to Sprint
                  </Button>
                )}
                {!isArchive && !isTriage && !isKanbanView && framework === WorkspaceFramework.kanban && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.sprintStatus, 'kanban')}
                  >
                    <ProjectOutlined className="w-4 h-4" />
                    Add to Kanban
                  </Button>
                )}
                {!isArchive && (
                  <Button
                    size="large"
                    className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
                    onClick={() => onBatchEdit(EditField.stage, 'archive')}
                  >
                    <FolderOutlined className="w-4 h-4" />
                    Archive
                  </Button>
                )}
              </div>
            )}
            <Button
              size="large"
              type="text"
              danger
              className="rounded-lg items-center gap-2 flex text-red-600 text-base font-semibold"
              onClick={onDeleteSelected}
            >
              <DeleteOutlined className="w-4 h-4" />
              {isArchive ? 'Delete Forever' : 'Delete'}
            </Button>
          </div>
          <div className="w-px h-[42px] bg-neutral-300 rounded-sm" />
          <div className="p-0.5 rounded justify-start items-start gap-2.5 flex">
            <CustomTooltip title="Deselect All Tasks">
              <Button type="text" className="w-5" onClick={() => setSelectedItems([])}>
                <CloseOutlined />
              </Button>
            </CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPanel;
